/**
 * SPARNINGEN — ett tryck på Spara, hur många försök som helst, SAMMA op_id.
 *
 * BESLUT 09, ORDAGRANT UR api/klient.ts: "Mynta ETT [op_id] med nyUlid() när
 * användaren trycker, spara det med det du ska skicka, och använd det tills
 * anropet lyckats." Ett nytt op_id per försök gör varje omsändning till en NY
 * skrivning — tre tryck på "Försök igen" efter ett tappat nät ger då tre
 * åttatimmarsdagar i underlaget. Kroken nedan håller därför operationerna kvar
 * mellan försöken och bygger dem BARA en gång.
 *
 * TVÅ SORTERS FEL, OCH DE BETYDER OLIKA SAKER (shared/errors.ts):
 *   `fel`     HELA anropet föll — utloggad, låsläge, nätet, för stort kuvert.
 *             INGEN operation tillämpades, allt ligger kvar oförändrat och ska
 *             skickas om med samma op_id.
 *   `fallna`  Kuvertet gick igenom (HTTP 200) men enskilda operationer föll.
 *             Kontraktet garanterar att en fallen operation inte rullar tillbaka
 *             en tidigare — de som lyckades är sparade. Bara de fallna ligger
 *             kvar, och de flesta av deras koder är 'give_up': samma operation
 *             kan inte lyckas oförändrad, så användaren måste rätta något.
 *
 * DÄRFÖR TÖMS INTE UTKORGEN VID `fel`, MEN VID EN FALLEN 'give_up'-OPERATION
 * MÅSTE ANVÄNDAREN RÄTTA. Den fulla utkorgen med "Behöver åtgärd" byggs i steg 5;
 * här räknas det som att formuläret står kvar ifyllt med felet synligt.
 */

import { useCallback, useRef, useState } from 'react';
import type { SyncOp, SyncOpResult, SyncPushResponse } from '@delat/types';
import { apifel } from '../../api';
import type { Klartidfel, Resultat } from '../../api';

export type FallenOperation = Extract<SyncOpResult, { status: 'failed' }>;

const arFallen = (rad: SyncOpResult): rad is FallenOperation => rad.status === 'failed';

/**
 * En fallen operations fel som ett Klartidfel, så att <Felruta> kan visa det.
 *
 * TEXTEN TAS UR shared/errors.ts OCH INTE UR SVARET — apifel() slår upp
 * ERRORS[code].message_sv själv (api/fel.ts): en gammal eller manipulerad server
 * ska inte kunna skriva text rakt in i gränssnittet.
 *
 * DE TVÅ SLUTNA DETALJERNA GRENAS UT var för sig. Det är inte en artighet mot
 * tsc utan hela skälet till att ApiErrorBody är en diskriminerad union: annars
 * går det att skicka vidare ett invoice_run_id eller ett price_list_id till en
 * montörstelefon (beslut 16 och 17).
 */
export const felAvOperation = (rad: FallenOperation): Klartidfel => {
  const kropp = rad.error;
  if (kropp.code === 'ROW_INVOICED') {
    return apifel('ROW_INVOICED', kropp.detail, kropp.message_sv);
  }
  if (kropp.code === 'PRICE_MISSING') {
    return apifel('PRICE_MISSING', kropp.detail, kropp.message_sv);
  }
  return apifel(kropp.code, kropp.detail, kropp.message_sv);
};

export interface Sparning {
  readonly sparar: boolean;
  /** Helanropsfel. Ingenting tillämpades. */
  readonly fel: Klartidfel | null;
  /** Operationer som föll i ett i övrigt lyckat kuvert. */
  readonly fallna: readonly FallenOperation[];
  /**
   * Bygger operationerna FÖRSTA gången och skickar. Varje senare anrop skickar
   * exakt samma operationer, med samma op_id, tills det lyckats.
   * Returnerar true när allt gick igenom.
   */
  readonly spara: (bygg: () => readonly SyncOp[]) => Promise<boolean>;
  /** Slänger de kvarliggande operationerna. Används när formuläret ändras. */
  readonly nollstall: () => void;
}

export const useSparning = (
  skicka: (ops: readonly SyncOp[]) => Promise<Resultat<SyncPushResponse>>,
): Sparning => {
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [fallna, sattFallna] = useState<readonly FallenOperation[]>([]);

  // Operationerna som väntar på att lyckas. Referens och inte tillstånd: de ska
  // överleva en omritning utan att själva utlösa en.
  const kvar = useRef<readonly SyncOp[] | null>(null);
  // `skicka` är en ny funktion vid varje omritning (useSynk bygger den). Utan
  // referensen hade `spara` bytt identitet lika ofta.
  const skickaRef = useRef(skicka);
  skickaRef.current = skicka;
  const pagar = useRef(false);

  /**
   * Idempotent med FLIT: skärmarna ropar den vid varje fältändring, så att
   * gamla operationer inte skickas om efter att formuläret ändrats. Satte den
   * om tillståndet varje gång skulle varje tangenttryckning rita om skärmen.
   */
  const nollstall = useCallback(() => {
    kvar.current = null;
    sattFel((tidigare) => (tidigare === null ? tidigare : null));
    sattFallna((tidigare) => (tidigare.length === 0 ? tidigare : []));
  }, []);

  const spara = useCallback(async (bygg: () => readonly SyncOp[]): Promise<boolean> => {
    if (pagar.current) return false;
    const ops = kvar.current ?? bygg();
    if (ops.length === 0) return true;
    kvar.current = ops;

    pagar.current = true;
    sattSparar(true);
    sattFel(null);
    sattFallna([]);
    try {
      const svar = await skickaRef.current(ops);
      if (!svar.ok) {
        sattFel(svar.fel);
        return false;
      }
      const trasiga = svar.varde.results.filter(arFallen);
      if (trasiga.length > 0) {
        sattFallna(trasiga);
        return false;
      }
      kvar.current = null;
      return true;
    } finally {
      pagar.current = false;
      sattSparar(false);
    }
  }, []);

  return { sparar, fel, fallna, spara, nollstall };
};
